import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, ChevronLeft, ChevronRight, UserRound } from 'lucide-react';
import Sidebar from './Sidebar';
import SidebarDrillTransition from './SidebarDrillTransition';
import SidebarUtilityStrip from './SidebarUtilityStrip';
import SidebarProfileMenu from './SidebarProfileMenu';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/users/profileService';
import { useQuery } from '@tanstack/react-query';
import { getRecentParentAthletes } from '../../pages/parent/parentAthleteNav';
import { previewPortalData } from '../../pages/parent/parentPortalData';
import { useSidebarDrill } from '../../contexts/SidebarDrillContext';

interface ParentSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '', hasSubItems: false },
  { id: 'athletes', label: 'Athletes', icon: UserRound, path: '/athlete', hasSubItems: true },
];

function parentDrillPanelForPath(pathname: string): string | null {
  if (/\/parent(?:\/preview)?\/athlete(?:\/|$)/.test(pathname)) return 'athletes';
  return null;
}

const ParentSidebar: React.FC<ParentSidebarProps> = ({ isCollapsed, onToggle }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarDrill = useSidebarDrill();
  const [activeDrillPanel, setActiveDrillPanel] = useState<string | null>(null);
  const [recentAthletes, setRecentAthletes] = useState(() => getRecentParentAthletes(3));

  const isPreview = location.pathname.startsWith('/parent/preview');
  const basePath = isPreview ? '/parent/preview' : '/parent';
  const navSignature = location.pathname;
  const drillPanelForLocation = useMemo(
    () => parentDrillPanelForPath(location.pathname),
    [location.pathname]
  );
  const prevNavSignatureRef = useRef(navSignature);

  const activeAthleteId = useMemo(() => {
    const match = location.pathname.match(/\/parent(?:\/preview)?\/athlete\/([^/]+)/);
    return match?.[1] ?? null;
  }, [location.pathname]);

  const { data: profile } = useQuery({
    queryKey: ['parent-profile', user?.id],
    queryFn: () => profileService.getProfile(user!.id),
    enabled: !!user?.id,
  });

  const displayName = useMemo(() => {
    const fullName = (profile?.full_name || '').trim();
    if (fullName) return fullName;
    const metadataName = String((user as any)?.user_metadata?.full_name || '').trim();
    if (metadataName) return metadataName;
    return 'Parent';
  }, [profile?.full_name, user]);

  const avatarLetter = displayName.charAt(0).toUpperCase() || 'P';

  const drillAthletes = useMemo(() => {
    const recent = recentAthletes;
    if (recent.length > 0) return recent;
    if (isPreview) {
      return previewPortalData.athletes.map((athlete) => ({ id: athlete.id, name: athlete.name }));
    }
    return [];
  }, [recentAthletes, isPreview]);

  const activeId =
    location.pathname === basePath
      ? 'dashboard'
      : location.pathname.startsWith(`${basePath}/athlete`)
        ? 'athletes'
        : undefined;

  useEffect(() => {
    setRecentAthletes(getRecentParentAthletes(3));
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarDrill) return;
    return sidebarDrill.registerDrillHandler((panelId) => {
      if (!isCollapsed) setActiveDrillPanel(panelId);
    });
  }, [sidebarDrill, isCollapsed]);

  useEffect(() => {
    if (isCollapsed) return;

    const prevSignature = prevNavSignatureRef.current;
    const navChanged = navSignature !== prevSignature;
    prevNavSignatureRef.current = navSignature;

    if (navChanged && drillPanelForLocation) {
      setActiveDrillPanel(drillPanelForLocation);
      return;
    }

    if (navChanged && !drillPanelForLocation && activeDrillPanel) {
      setActiveDrillPanel(null);
    }
  }, [isCollapsed, navSignature, drillPanelForLocation, activeDrillPanel]);

  const drillBackClass =
    'relative w-full flex items-center justify-center p-3 rounded-lg text-sm font-medium text-gray-800 hover:bg-gray-100 transition-colors';
  const drillItemClass =
    'w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors text-gray-700';
  const drillItemActiveClass = 'bg-gray-100 text-gray-900 font-medium';

  const handleMenuItemClick = (itemId: string, path: string, hasSubItems: boolean) => {
    navigate(`${basePath}${path}`);
    if (!isCollapsed && hasSubItems) {
      setActiveDrillPanel((prev) => (prev === itemId ? null : itemId));
    } else {
      setActiveDrillPanel(null);
    }
  };

  const activePanelMeta = navItems.find((item) => item.id === activeDrillPanel) || null;

  const profileContent = (
    <SidebarProfileMenu
      isCollapsed={isCollapsed}
      displayName={displayName}
      email={user?.email || ''}
      profilePath={`${basePath}/settings`}
      onSignOut={async () => {
        await signOut();
        navigate('/login');
      }}
      avatar={
        <span className="h-8 w-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
          {avatarLetter}
        </span>
      }
    />
  );

  return (
    <Sidebar
      isCollapsed={isCollapsed}
      onToggle={onToggle}
      homePath={basePath}
      navContent={
        <SidebarDrillTransition
          drillKey={!isCollapsed && activePanelMeta ? activeDrillPanel : null}
          drillMenu={
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setActiveDrillPanel(null)}
                className={drillBackClass}
              >
                <ChevronLeft className="absolute left-3 h-4 w-4" aria-hidden />
                <span>{activePanelMeta?.label ?? ''}</span>
              </button>

              {activeDrillPanel === 'athletes' && (
                <ul className="space-y-1">
                  <li className="px-3 py-1 text-xs font-semibold uppercase text-gray-500">
                    Recent athletes
                  </li>
                  {drillAthletes.length === 0 ? (
                    <li className="px-3 py-1 text-xs text-gray-500">No athletes linked yet</li>
                  ) : (
                    drillAthletes.map((athlete) => (
                      <li key={athlete.id}>
                        <button
                          type="button"
                          onClick={() => navigate(`${basePath}/athlete/${athlete.id}`)}
                          className={`${drillItemClass} ${
                            activeAthleteId === athlete.id ? drillItemActiveClass : ''
                          }`}
                        >
                          {athlete.name}
                        </button>
                      </li>
                    ))
                  )}
                  <li className="my-2 border-t border-gray-200" />
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate(`${basePath}/athlete`)}
                      className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-rowing-blue hover:bg-gray-100"
                    >
                      View all athletes
                    </button>
                  </li>
                </ul>
              )}
            </div>
          }
          rootMenu={
            <ul className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => handleMenuItemClick(item.id, item.path, item.hasSubItems)}
                      className={`relative flex w-full items-center rounded-lg p-3 transition-colors ${
                        activeId === item.id
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && (
                        <>
                          <span className="ml-3 font-medium">{item.label}</span>
                          {item.hasSubItems && (
                            <ChevronRight
                              className={`ml-auto h-4 w-4 text-gray-500 transition-transform ${
                                activeDrillPanel === item.id ? 'rotate-90' : ''
                              }`}
                            />
                          )}
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          }
        />
      }
      footerContent={
        <div className={`px-3 ${isCollapsed ? 'pt-1.5 pb-0' : 'pt-2 pb-0'}`}>
          <SidebarUtilityStrip
            isCollapsed={isCollapsed}
            persona="parent"
            notificationsPath={`${basePath}/notifications`}
            inboxPath={`${basePath}/inbox`}
            settingsPath={`${basePath}/settings`}
          />
        </div>
      }
      profileContent={profileContent}
    />
  );
};

export default ParentSidebar;
